import type { ContextOnSubmit } from '@feathery/react/dist/types/Form';
import type {
  CrbMarketingUserOnboardingFormDataV1_2,
  CrbMarketingUserOnboardingFormDataV1_3,
  DueDiligenceStatus,
  InputMarketingOrgRegistration,
  User
} from '@gcv/shared';
import { FeatheryForm, FeatheryPlantTouchingBusinessStage, OrganizationStage } from '@gcv/shared';
import { PlantTouchingIndustryRoles, USStates } from '@gcv/shared';
import { inject, injectable } from 'inversify';
import { action, makeAutoObservable, observe } from 'mobx';

import { CrbBankStore } from 'stores/CrbBankStore';
import { CrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { UserStore } from 'stores/UserStore';
import { USER_KEY } from 'tanstack-hooks/keys';
import { updatePendoForCrb } from 'third-party-integrations/pendo';
import { stripCountryCode } from 'util/phone.util';
import type { PM } from './welcome.repo';
import { CrbWelcomeRepo } from './welcome.repo';

interface VM {
  isLoading: boolean;
  formData: CrbMarketingUserOnboardingFormDataV1_2 | CrbMarketingUserOnboardingFormDataV1_3;
  user: null | User;
  formId: FeatheryForm;
}

@injectable()
export class CrbWelcomePresenter {
  @inject(CrbWelcomeRepo)
  repo: CrbWelcomeRepo;

  @inject(CrbDispensaryStore)
  dispensaryStore: CrbDispensaryStore;

  @inject(UserStore)
  userStore: UserStore;

  @inject(CrbBankStore)
  crbBankStore: CrbBankStore;

  vm: VM = {
    isLoading: true,
    formData: {},
    user: null,
    formId: FeatheryForm.CRB_Marketing_User_Onboarding_1_3
  };

  constructor() {
    makeAutoObservable(this);
  }

  public load = async () => {
    observe(this.repo, 'programmersModel', (obj) => {
      const programmersModel = obj.newValue as PM;

      this.vm.formData = programmersModel.formData;
    });

    this.vm.user = this.userStore.user;

    this.repo.load(this.vm.formId).then(() => {
      this.setIsLoading(false);
    });
  };

  private isPlantTouching = (role: string) => role in PlantTouchingIndustryRoles;

  public onSubmit = async (context: ContextOnSubmit): Promise<boolean> => {
    const formData = context.getFieldValues() as CrbMarketingUserOnboardingFormDataV1_3;
    const plantTouching = this.isPlantTouching(formData['business-role_2']?.[0] ?? '');

    let organizationStage = OrganizationStage.ACTIVE;
    if (plantTouching && formData['phase-L1']?.[0]) {
      switch (formData['phase-L1']?.[0]) {
        case FeatheryPlantTouchingBusinessStage['Approved provisionally (seedling)']:
        case FeatheryPlantTouchingBusinessStage["Haven't applied for a license yet"]:
          organizationStage = OrganizationStage.PENDING_LICENSE;
          break;
        case FeatheryPlantTouchingBusinessStage['Licensed but not operating']:
          organizationStage = OrganizationStage.LICENSED_BUT_NOT_OPERATIONAL;
          break;
        case FeatheryPlantTouchingBusinessStage['Operating, and trying to expand']:
        case FeatheryPlantTouchingBusinessStage['Fully licensed and operating!']:
        case FeatheryPlantTouchingBusinessStage['Top brand in the space']:
          organizationStage = OrganizationStage.ACTIVE;
          break;
        default:
          break;
      }
    }

    const data: InputMarketingOrgRegistration = {
      termsOfService: Boolean(formData['terms-of-service_2']),
      companyName: formData['business-name_2']?.trim() ?? '',
      phone: formData.phone_number_1 ? stripCountryCode(formData.phone_number_1) : undefined, // feathery includes the country code, therefore exclude it here
      plantTouching,
      state: USStates[formData['State_Dropdown']?.replaceAll(' ', '_') as keyof typeof USStates],
      userInterests: this.vm.user?.interests ?? [],
      organizationStage,
      monthlyCustomers: 0
    };

    if (context.isLastStep()) {
      await this.repo.registerMarketingUserOrg(data);
      await this.userStore.queryClient.invalidateQueries({ queryKey: [USER_KEY, this.userStore.user?.id] });
      await this.userStore.load(this.userStore.user.id);

      const dispensaryId = this.userStore.user.companies[0].id;
      await this.dispensaryStore.loadDispensaries(this.userStore.user.id);
      await this.dispensaryStore.setCurrentDispensary(dispensaryId);

      const provider = this.dispensaryStore.getDefaultProvider();
      const activeServiceProviders = this.crbBankStore.getActiveCrbServiceProviders();
      const showCompetitiveMarketPlaceOffers = activeServiceProviders.some(
        (sp) => sp.showCompetitors || sp.activeServiceProvider === false
      );
      updatePendoForCrb(
        this.dispensaryStore.currentDispensary,
        this.userStore.user,
        this.dispensaryStore.currentDispensary.name,
        provider.id,
        provider.templates.value.onboarding.status as DueDiligenceStatus,
        activeServiceProviders.length,
        activeServiceProviders,
        showCompetitiveMarketPlaceOffers,
        {
          role: formData['business-role_2'],
          userInterests: this.vm.user?.interests ?? [],
          banking_status: formData['banking-status_2'],
          is_plant_touching: this.dispensaryStore.currentDispensaryIsPlantTouching
        }
      );
      return true;
    }
    return false;
  };

  onChange = (formData: Record<string, unknown>) => {
    if (this.vm.formId === FeatheryForm.CRB_Marketing_User_Onboarding_1_2) {
      this.repo.updateUserFeatheryFormResponseOld(formData, this.vm.formId);
    } else {
      this.repo.updateUserFeatheryFormResponse(formData, this.vm.formId);
    }
  };

  setIsLoading = action((isLoading: boolean) => {
    this.vm.isLoading = isLoading;
  });

  setFormData = (formData: Record<string, unknown>) => {
    this.vm.formData = formData;
  };
}
